import platform from 'platform';
import { useCallback, useEffect, useRef } from 'react';

import {
  useFindChallengeWebcamLogMutation,
  useFindWebcamLogByUrlMutation,
  useSendMediaMutation,
  useSendWebcamLogMutation,
} from '../certificationApiSlice';
import { buildExamMediaRecorderOptions } from '../utils/mediaConstraints';
import type {
  ChallengeClipFinishInput,
  ChallengeClipStartInput,
  RecorderDiagnosticEvent,
  RecorderStateEvent,
  RecorderTerminalEvent,
  UseLivenessClipRecorderParams,
  UseLivenessClipRecorderReturn,
} from './types';

type RecorderState = 'idle' | 'recording' | 'finalizing' | 'queued';

type ActiveSession = {
  sessionRef: string;
  challengeType: ChallengeClipStartInput['challengeType'];
  recorder: MediaRecorder;
  streamClone: MediaStream;
  chunks: Blob[];
  audioAvailable: boolean;
  finalized: boolean;
};

type QueueItem = {
  sessionRef: string;
  challenge: ChallengeClipFinishInput;
  blob: Blob;
  audioAvailable: boolean;
  contextWindowSeconds: number;
  attempts: number;
};

type BaseWebcamPayload = {
  _id: string;
  time: number;
  url: string;
  type: 'video';
  size: number;
  certification_log?: string;
  player?: string;
  device_info?: string;
};

const RETRY_BACKOFF_MS = [2000, 5000, 10000, 30000] as const;

function createSessionRef() {
  return `liveness_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
}

function stopTracks(stream: MediaStream | null | undefined) {
  if (!stream) return;

  stream.getTracks().forEach(track => {
    try {
      track.stop();
    } catch {
      // noop
    }
  });
}

function getActiveAudioTrack(stream: MediaStream | null | undefined) {
  if (!stream) return null;

  const track = stream
    .getAudioTracks()
    .find(audioTrack => audioTrack.readyState !== 'ended');

  return track ?? null;
}

function wait(ms: number) {
  return new Promise<void>(resolve => {
    const timeoutFn =
      (globalThis as { setTimeout?: unknown }).setTimeout ??
      (globalThis as { window?: { setTimeout?: unknown } }).window
        ?.setTimeout ??
      (globalThis as { global?: { setTimeout?: unknown } }).global?.setTimeout;

    if (typeof timeoutFn !== 'function') {
      resolve();
      return;
    }

    timeoutFn(resolve, ms);
  });
}

function getBackoffMs(attempt: number) {
  const index = Math.min(attempt - 1, RETRY_BACKOFF_MS.length - 1);
  return (
    RETRY_BACKOFF_MS[index] ?? RETRY_BACKOFF_MS[RETRY_BACKOFF_MS.length - 1]
  );
}

function resolveRecorderMimeType(recorder: MediaRecorder) {
  return recorder.mimeType || 'video/mp4';
}

async function stopRecorderAndAwaitFinalChunk(recorder: MediaRecorder) {
  if (recorder.state === 'inactive') {
    return;
  }

  await new Promise<void>(resolve => {
    let settled = false;
    const finalize = () => {
      if (settled) {
        return;
      }
      settled = true;
      resolve();
    };

    const mediaRecorder = recorder as MediaRecorder & {
      onstop: ((this: MediaRecorder, ev: Event) => unknown) | null;
    };

    const previousOnStop = mediaRecorder.onstop;
    mediaRecorder.onstop = function onStop(event: Event) {
      if (typeof previousOnStop === 'function') {
        previousOnStop.call(this, event);
      }
      finalize();
    };

    try {
      recorder.stop();
    } catch {
      finalize();
      return;
    }

    wait(1000).then(finalize);
  });
}

function isClientError(error: unknown) {
  const maybeError = error as { status?: unknown } | undefined;
  let maybeStatus = Number.NaN;

  if (typeof maybeError?.status === 'number') {
    maybeStatus = maybeError.status;
  } else if (typeof maybeError?.status === 'string') {
    maybeStatus = Number(maybeError.status);
  }

  return (
    Number.isFinite(maybeStatus) && maybeStatus >= 400 && maybeStatus < 500
  );
}

function resolveErrorStatus(error: unknown) {
  const maybeError = error as { status?: unknown } | undefined;
  if (typeof maybeError?.status === 'number') {
    return maybeError.status;
  }
  if (typeof maybeError?.status === 'string') {
    const parsed = Number(maybeError.status);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function useLivenessClipRecorder({
  stream,
  certificationLogId,
  candidateId,
  preRollMs = 5000,
  postRollMs = 5000,
  maxRetryAttempts,
  onStateChange,
  onTerminalState,
  onDiagnostic,
}: UseLivenessClipRecorderParams): UseLivenessClipRecorderReturn {
  const [sendMedia] = useSendMediaMutation();
  const [sendWebcamLog] = useSendWebcamLogMutation();
  const [findChallengeWebcamLog] = useFindChallengeWebcamLogMutation();
  const [findWebcamLogByUrl] = useFindWebcamLogByUrlMutation();
  const logRecorderDebugRef = useRef<
    (event: string, details?: unknown) => void
  >(() => undefined);

  const resolvedMaxRetryAttempts = Math.max(1, maxRetryAttempts ?? 4);

  const stateRef = useRef<RecorderState>('idle');
  const activeSessionRef = useRef<ActiveSession | null>(null);
  const queueRef = useRef<QueueItem[]>([]);
  const processingRef = useRef<Promise<void> | null>(null);
  const processingItemRef = useRef<QueueItem | null>(null);
  const droppedSessionsRef = useRef<Set<string>>(new Set());
  const fallbackAudioStreamRef = useRef<MediaStream | null>(null);
  const fallbackAudioRequestRef = useRef<Promise<void> | null>(null);

  const emitState = useCallback(
    (to: RecorderState, sessionRef: string, reason?: string) => {
      const from = stateRef.current;
      stateRef.current = to;

      const payload: RecorderStateEvent = {
        sessionRef,
        from,
        to,
        ...(reason ? { reason } : {}),
      };

      logRecorderDebugRef.current('state_transition', payload);
      onStateChange?.(payload);
    },
    [onStateChange],
  );

  const emitTerminal = useCallback(
    (event: RecorderTerminalEvent) => {
      logRecorderDebugRef.current('terminal_event', event);
      onTerminalState?.(event);
    },
    [onTerminalState],
  );

  const emitDiagnostic = useCallback(
    (event: RecorderDiagnosticEvent) => {
      logRecorderDebugRef.current('diagnostic_event', event);
      onDiagnostic?.(event);
    },
    [onDiagnostic],
  );

  const emitDroppedTerminal = useCallback(
    (item: QueueItem) => {
      if (droppedSessionsRef.current.has(item.sessionRef)) {
        return;
      }

      droppedSessionsRef.current.add(item.sessionRef);
      emitDiagnostic({
        code: 'dropped_on_unmount',
        sessionRef: item.sessionRef,
        message: 'Pending liveness clip was dropped before upload completion.',
      });
      emitTerminal({
        sessionRef: item.sessionRef,
        terminalState: 'dropped_on_unmount',
        challengeId: item.challenge.challengeId,
        evidenceRef: item.challenge.evidenceRef ?? null,
      });
    },
    [emitDiagnostic, emitTerminal],
  );
  const emitDiagnosticRef = useRef(emitDiagnostic);
  emitDiagnosticRef.current = emitDiagnostic;
  const emitTerminalRef = useRef(emitTerminal);
  emitTerminalRef.current = emitTerminal;
  const emitDroppedTerminalRef = useRef(emitDroppedTerminal);
  emitDroppedTerminalRef.current = emitDroppedTerminal;

  const ensureFallbackAudioStream = useCallback(async () => {
    if (getActiveAudioTrack(stream)) {
      return;
    }

    if (getActiveAudioTrack(fallbackAudioStreamRef.current)) {
      return;
    }

    if (fallbackAudioRequestRef.current) {
      await fallbackAudioRequestRef.current;
      return;
    }

    const mediaDevices = globalThis.navigator?.mediaDevices;
    if (!mediaDevices?.getUserMedia) {
      return;
    }

    fallbackAudioRequestRef.current = mediaDevices
      .getUserMedia({ audio: true, video: false })
      .then(audioStream => {
        stopTracks(fallbackAudioStreamRef.current);
        fallbackAudioStreamRef.current = audioStream;
      })
      .catch(() => undefined)
      .finally(() => {
        fallbackAudioRequestRef.current = null;
      });

    await fallbackAudioRequestRef.current;
  }, [stream]);

  useEffect(() => {
    ensureFallbackAudioStream().catch(() => undefined);
  }, [ensureFallbackAudioStream]);

  useEffect(() => {
    return () => {
      stopTracks(fallbackAudioStreamRef.current);
      fallbackAudioStreamRef.current = null;
    };
  }, []);

  const uploadMediaClip = useCallback(
    async (item: QueueItem) => {
      const extra = {
        // `upload/file` must stay on the generic webcam session here.
        // The challenge identity is persisted by the explicit webcam_log__c
        // write below; using a dedicated upload session causes the backend to
        // create an additional generic record for the same URL.
        session: 'webcam',
        type: 'video',
        ...(certificationLogId
          ? { certification_log: certificationLogId }
          : {}),
        ...(candidateId ? { player: candidateId } : {}),
        ...(platform.description ? { device_info: platform.description } : {}),
      };

      const formData = new FormData();
      formData.append('file', item.blob, `${item.sessionRef}.mp4`);
      formData.append('extra', JSON.stringify(extra));
      logRecorderDebugRef.current('media_upload_start', {
        sessionRef: item.sessionRef,
        blobSize: item.blob.size,
        challengeId: item.challenge.challengeId,
        challengeStatus: item.challenge.challengeStatus,
        attempt: item.attempts + 1,
      });

      const mediaResult = await sendMedia(formData).unwrap();
      const uploadUrl = mediaResult?.uploads?.[0]?.url;

      if (!uploadUrl) {
        logRecorderDebugRef.current('media_upload_missing_url', {
          sessionRef: item.sessionRef,
          challengeId: item.challenge.challengeId,
        });
        throw new Error('upload_url_missing');
      }

      logRecorderDebugRef.current('media_upload_success', {
        sessionRef: item.sessionRef,
        uploadUrl,
      });

      return uploadUrl;
    },
    [candidateId, certificationLogId, sendMedia],
  );

  const sendFullWebcamPayload = useCallback(
    async (item: QueueItem, uploadUrl: string) => {
      let resolvedWebcamLogId = item.sessionRef;

      try {
        logRecorderDebugRef.current('webcam_log_url_probe_start', {
          sessionRef: item.sessionRef,
          challengeId: item.challenge.challengeId,
          uploadUrl,
        });
        const existingLogs = await findWebcamLogByUrl({
          url: uploadUrl,
          certificationLogId: certificationLogId || undefined,
          playerId: candidateId || undefined,
        }).unwrap();
        const existingLogId = existingLogs
          .map(record => record?._id)
          .find((id): id is string => typeof id === 'string' && id.length > 0);

        if (existingLogId) {
          resolvedWebcamLogId = existingLogId;
        }

        logRecorderDebugRef.current('webcam_log_url_probe_result', {
          sessionRef: item.sessionRef,
          challengeId: item.challenge.challengeId,
          uploadUrl,
          found: Boolean(existingLogId),
          resolvedWebcamLogId,
        });
      } catch {
        logRecorderDebugRef.current('webcam_log_url_probe_error', {
          sessionRef: item.sessionRef,
          challengeId: item.challenge.challengeId,
          uploadUrl,
        });
      }

      const basePayload: BaseWebcamPayload = {
        _id: resolvedWebcamLogId,
        time: new Date(item.challenge.completedAtIso).getTime(),
        url: uploadUrl,
        type: 'video',
        size: item.blob.size,
        ...(certificationLogId
          ? { certification_log: certificationLogId }
          : {}),
        ...(candidateId ? { player: candidateId } : {}),
        ...(platform.description ? { device_info: platform.description } : {}),
      };

      const fullPayload = {
        ...basePayload,
        evidence_ref: item.challenge.evidenceRef ?? null,
        challenge_id: item.challenge.challengeId,
        challenge_type: item.challenge.challengeType,
        challenge_status: item.challenge.challengeStatus,
        clip_role: 'liveness_challenge',
        context_window_seconds: item.contextWindowSeconds,
        upload_attempts: item.attempts,
        audio_available: item.audioAvailable,
      };

      logRecorderDebugRef.current('webcam_log_full_payload_start', {
        sessionRef: item.sessionRef,
        challengeId: item.challenge.challengeId,
        uploadUrl,
        uploadAttempts: item.attempts,
        resolvedWebcamLogId,
      });

      await sendWebcamLog(
        fullPayload as Parameters<typeof sendWebcamLog>[0],
      ).unwrap();
      logRecorderDebugRef.current('webcam_log_full_payload_success', {
        sessionRef: item.sessionRef,
        challengeId: item.challenge.challengeId,
        uploadUrl,
        resolvedWebcamLogId,
      });
    },
    [candidateId, certificationLogId, findWebcamLogByUrl, sendWebcamLog],
  );

  const hasExistingChallengeWebcamLog = useCallback(
    async (item: QueueItem, uploadUrl: string) => {
      if (!item.challenge.challengeId) {
        return false;
      }

      try {
        logRecorderDebugRef.current('webcam_log_probe_start', {
          sessionRef: item.sessionRef,
          challengeId: item.challenge.challengeId,
          uploadUrl,
        });
        const records = await findChallengeWebcamLog({
          url: uploadUrl,
          challengeId: item.challenge.challengeId,
          certificationLogId: certificationLogId || undefined,
        }).unwrap();

        const found = Array.isArray(records) && records.length > 0;
        logRecorderDebugRef.current('webcam_log_probe_result', {
          sessionRef: item.sessionRef,
          challengeId: item.challenge.challengeId,
          uploadUrl,
          found,
          count: Array.isArray(records) ? records.length : 0,
        });
        return found;
      } catch {
        logRecorderDebugRef.current('webcam_log_probe_error', {
          sessionRef: item.sessionRef,
          challengeId: item.challenge.challengeId,
          uploadUrl,
        });
        return false;
      }
    },
    [certificationLogId, findChallengeWebcamLog],
  );

  const processItem = useCallback(
    async (item: QueueItem) => {
      while (item.attempts < resolvedMaxRetryAttempts) {
        if (droppedSessionsRef.current.has(item.sessionRef)) {
          return;
        }

        item.attempts += 1;
        logRecorderDebugRef.current('queue_attempt_start', {
          sessionRef: item.sessionRef,
          challengeId: item.challenge.challengeId,
          attempt: item.attempts,
        });

        const uploadResult = await uploadMediaClip(item)
          .then(url => ({ ok: true as const, url }))
          .catch(uploadError => {
            logRecorderDebugRef.current('media_upload_error', {
              sessionRef: item.sessionRef,
              challengeId: item.challenge.challengeId,
              attempt: item.attempts,
              error:
                uploadError instanceof Error
                  ? uploadError.message
                  : String(uploadError),
            });
            return { ok: false as const };
          });

        if (!uploadResult.ok) {
          if (item.attempts >= resolvedMaxRetryAttempts) {
            emitTerminal({
              sessionRef: item.sessionRef,
              terminalState: 'failed',
              challengeId: item.challenge.challengeId,
              evidenceRef: item.challenge.evidenceRef ?? null,
            });
            return;
          }

          await wait(getBackoffMs(item.attempts));
        } else {
          if (droppedSessionsRef.current.has(item.sessionRef)) {
            return;
          }

          const uploadUrl = uploadResult.url;

          try {
            await sendFullWebcamPayload(item, uploadUrl);
            emitTerminal({
              sessionRef: item.sessionRef,
              terminalState: 'uploaded',
              challengeId: item.challenge.challengeId,
              evidenceRef: item.challenge.evidenceRef ?? null,
            });
            return;
          } catch (webcamError) {
            logRecorderDebugRef.current('webcam_log_full_payload_error', {
              sessionRef: item.sessionRef,
              challengeId: item.challenge.challengeId,
              uploadUrl,
              attempt: item.attempts,
              status: resolveErrorStatus(webcamError),
              error:
                webcamError instanceof Error
                  ? webcamError.message
                  : String(webcamError),
            });
            if (isClientError(webcamError)) {
              const existingRecordFound = await hasExistingChallengeWebcamLog(
                item,
                uploadUrl,
              );
              if (existingRecordFound) {
                emitDiagnostic({
                  code: 'minimal_payload_skipped_existing_record',
                  sessionRef: item.sessionRef,
                  message:
                    'Full webcam payload likely persisted despite 4xx. Skipping minimal fallback to avoid duplicate clip records.',
                });
                emitTerminal({
                  sessionRef: item.sessionRef,
                  terminalState: 'uploaded',
                  challengeId: item.challenge.challengeId,
                  evidenceRef: item.challenge.evidenceRef ?? null,
                });
                return;
              }

              emitDiagnostic({
                code: 'full_payload_rejected',
                sessionRef: item.sessionRef,
                message:
                  'Full webcam payload was rejected with 4xx and no persisted challenge record was found. Failing the upload to avoid duplicate clip records.',
              });
              emitTerminal({
                sessionRef: item.sessionRef,
                terminalState: 'failed',
                challengeId: item.challenge.challengeId,
                evidenceRef: item.challenge.evidenceRef ?? null,
              });
              return;
            }

            if (item.attempts >= resolvedMaxRetryAttempts) {
              emitTerminal({
                sessionRef: item.sessionRef,
                terminalState: 'failed',
                challengeId: item.challenge.challengeId,
                evidenceRef: item.challenge.evidenceRef ?? null,
              });
              return;
            }

            await wait(getBackoffMs(item.attempts));
          }
        }
      }
    },
    [
      emitDiagnostic,
      emitTerminal,
      resolvedMaxRetryAttempts,
      sendFullWebcamPayload,
      hasExistingChallengeWebcamLog,
      uploadMediaClip,
    ],
  );

  const processQueue = useCallback(async () => {
    if (processingRef.current) {
      return processingRef.current;
    }

    processingRef.current = (async () => {
      while (queueRef.current.length > 0) {
        const next = queueRef.current[0];

        if (!next) {
          break;
        }

        processingItemRef.current = next;

        await processItem(next);

        if (queueRef.current[0]?.sessionRef === next.sessionRef) {
          queueRef.current.shift();
        }

        processingItemRef.current = null;
        droppedSessionsRef.current.delete(next.sessionRef);
      }
    })();

    try {
      await processingRef.current;
    } finally {
      processingRef.current = null;
      if (!activeSessionRef.current && queueRef.current.length === 0) {
        stateRef.current = 'idle';
      }
    }
  }, [processItem]);

  const startChallengeClip = useCallback(
    (input: ChallengeClipStartInput) => {
      if (activeSessionRef.current) {
        emitDiagnostic({
          code: 'overlapping_challenge_start',
          sessionRef: activeSessionRef.current.sessionRef,
          message: 'A challenge recording session is already active.',
        });
        return activeSessionRef.current.sessionRef;
      }

      const sessionRef = createSessionRef();

      if (!stream) {
        emitDiagnostic({
          code: 'stream_unavailable',
          sessionRef,
          message: 'No exam stream available for challenge recording.',
        });
        emitTerminal({
          sessionRef,
          terminalState: 'skipped',
        });
        return sessionRef;
      }

      const streamClone = stream.clone();
      if (streamClone.getAudioTracks().length === 0) {
        const fallbackTrack = getActiveAudioTrack(
          fallbackAudioStreamRef.current,
        );
        if (fallbackTrack) {
          try {
            streamClone.addTrack(fallbackTrack.clone());
          } catch {
            // noop
          }
        } else {
          ensureFallbackAudioStream().catch(() => undefined);
        }
      }

      const audioAvailable = streamClone.getAudioTracks().length > 0;

      try {
        const recorder = new MediaRecorder(
          streamClone,
          buildExamMediaRecorderOptions(),
        );
        const chunks: Blob[] = [];

        recorder.ondataavailable = event => {
          if (event.data) {
            chunks.push(event.data);
          }
        };

        recorder.start();

        activeSessionRef.current = {
          sessionRef,
          challengeType: input.challengeType,
          recorder,
          streamClone,
          chunks,
          audioAvailable,
          finalized: false,
        };

        emitState('recording', sessionRef);

        return sessionRef;
      } catch {
        stopTracks(streamClone);

        emitDiagnostic({
          code: 'parallel_recorder_unsupported',
          sessionRef,
          message: 'Parallel recorder is not supported for this stream.',
        });
        emitTerminal({
          sessionRef,
          terminalState: 'skipped',
        });

        return sessionRef;
      }
    },
    [
      emitDiagnostic,
      emitState,
      emitTerminal,
      ensureFallbackAudioStream,
      stream,
    ],
  );

  const finishChallengeClip = useCallback(
    async (input: ChallengeClipFinishInput) => {
      const activeSession = activeSessionRef.current;

      if (!activeSession || activeSession.finalized) {
        return;
      }

      activeSession.finalized = true;
      emitState('finalizing', activeSession.sessionRef);

      await stopRecorderAndAwaitFinalChunk(activeSession.recorder);

      const clipBlob =
        activeSession.chunks.length > 0
          ? new Blob(activeSession.chunks, {
              type: resolveRecorderMimeType(activeSession.recorder),
            })
          : new Blob([], {
              type: resolveRecorderMimeType(activeSession.recorder),
            });

      const contextWindowSeconds = Math.round((preRollMs + postRollMs) / 1000);

      queueRef.current.push({
        sessionRef: activeSession.sessionRef,
        challenge: input,
        blob: clipBlob,
        audioAvailable: activeSession.audioAvailable,
        contextWindowSeconds,
        attempts: 0,
      });

      emitState('queued', activeSession.sessionRef);
      stopTracks(activeSession.streamClone);
      activeSessionRef.current = null;

      processQueue().catch(() => undefined);
    },
    [emitState, postRollMs, preRollMs, processQueue],
  );

  const flushQueue = useCallback(
    async (drainMs = 3000) => {
      processQueue().catch(() => undefined);

      const processingPromise = processingRef.current;
      if (!processingPromise) {
        return;
      }

      let timeoutReached = false;

      await Promise.race([
        processingPromise,
        wait(drainMs).then(() => {
          timeoutReached = true;
        }),
      ]);

      if (!timeoutReached) {
        return;
      }

      const currentItem = processingItemRef.current;
      const pendingItems = [...queueRef.current];

      queueRef.current = [];

      const allPending = [
        ...(currentItem ? [currentItem] : []),
        ...pendingItems.filter(
          item => item.sessionRef !== currentItem?.sessionRef,
        ),
      ];

      allPending.forEach(item => {
        emitDroppedTerminal(item);
      });
    },
    [emitDroppedTerminal, processQueue],
  );

  useEffect(() => {
    return () => {
      const activeSession = activeSessionRef.current;

      if (activeSession && !activeSession.finalized) {
        stopTracks(activeSession.streamClone);
        emitDiagnosticRef.current({
          code: 'dropped_on_unmount',
          sessionRef: activeSession.sessionRef,
          message: 'Active liveness clip was dropped on unmount.',
        });
        emitTerminalRef.current({
          sessionRef: activeSession.sessionRef,
          terminalState: 'dropped_on_unmount',
        });
      }

      const currentItem = processingItemRef.current;
      const pendingItems = [...queueRef.current];
      queueRef.current = [];

      const allPending = [
        ...(currentItem ? [currentItem] : []),
        ...pendingItems.filter(
          item => item.sessionRef !== currentItem?.sessionRef,
        ),
      ];

      allPending.forEach(item => {
        emitDroppedTerminalRef.current(item);
      });
    };
  }, []);

  return {
    startChallengeClip,
    finishChallengeClip,
    flushQueue,
    getQueueSize: () => queueRef.current.length,
  };
}
